import type { ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAppDispatch, useAppSelector, selectPermissions, selectUser } from '@/app/store';
import { setSidebarOpen, toggleSidebarCollapsed } from '@/app/slices/uiSlice';
import { useAuth } from '@/lib/auth/useAuth';
import { toPermissionSet, canAny } from '@/lib/auth/permissions';
import { api } from '@/lib/api/client';
import { XyraMark } from '@/features/auth/XyraMark';
import { useBranding } from '@/lib/auth/useBranding';
import { Tooltip } from '@/components/ui/Tooltip';
import { NAV, type NavIcon, type NavItem } from './navigation';
import { cn } from '@/lib/cn';

/**
 * Workspace sidebar — the dark navy rail from the Meridian shell designs
 * (workspace-dashboard.html). Light-gray icon+label items on `primary-900`; the
 * active route gets a brass left-accent and a lighter panel.
 *
 * Responsive per the brief: a fixed rail from `lg` up, an overlay drawer below.
 * Items the user lacks permission for are omitted entirely rather than shown
 * disabled — a nav full of dead links is worse than a shorter nav.
 */
export function Sidebar() {
  const dispatch = useAppDispatch();
  const open = useAppSelector((s) => s.ui.sidebarOpen);
  const collapsed = useAppSelector((s) => s.ui.sidebarCollapsed);
  const permissions = useAppSelector(selectPermissions);
  const user = useAppSelector(selectUser);
  const { logout } = useAuth();
  const granted = toPermissionSet(permissions);
  const { branding, isBranded } = useBranding();

  // The switcher card shows the real workspace + plan, not a hardcoded label.
  const tenant = useQuery({
    queryKey: ['tenant', 'current'],
    queryFn: () => api.get<{ name: string; plan?: string }>('/api/v1/tenants/current'),
    staleTime: 5 * 60_000,
  });

  const visible = (item: NavItem) => !item.permission || canAny(granted, item.permission);
  const sectionVisible = (s: (typeof NAV)[number]) => !s.roles || s.roles.some((r) => user?.roles?.includes(r));
  const sections = NAV.filter(sectionVisible)
    .map((s) => ({ ...s, items: s.items.filter(visible) }))
    .filter((s) => s.items.length > 0);

  const close = () => dispatch(setSidebarOpen(false));

  return (
    <>
      {/* Mobile scrim */}
      {open && (
        <div
          className="fixed inset-0 z-30 bg-ink-950/50 lg:hidden"
          onClick={close}
          aria-hidden="true"
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex flex-col bg-primary-900 text-white',
          'transition-[width,transform] duration-200 lg:translate-x-0',
          collapsed ? 'lg:w-16' : 'w-52',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
        aria-label="Main navigation"
      >
        {/* Brand */}
        <Tooltip label={isBranded ? branding?.companyName || 'Workspace' : 'XYRA — Revenue Suite'} disabled={!collapsed}>
          <div className={cn('flex items-center gap-2.5 px-5 pb-4 pt-5', collapsed && 'lg:justify-center lg:px-0')}>
            {isBranded && branding?.logoUrl ? (
              <img
                src={branding.logoUrl}
                alt={branding.companyName || 'Logo'}
                className={cn('h-7 w-auto shrink-0 object-contain', collapsed && 'lg:hidden')}
              />
            ) : (
              <XyraMark className={cn('h-7 w-auto shrink-0', collapsed && 'lg:hidden')} />
            )}
            {collapsed && (isBranded && branding?.logoUrl ? (
              <img src={branding.logoUrl} alt={branding.companyName || 'Logo'} className="hidden h-8 w-8 shrink-0 object-contain lg:block" />
            ) : (
              <XyraMark className="hidden h-8 w-8 shrink-0 lg:block" compact />
            ))}
            <div className={cn('min-w-0 leading-tight', collapsed && 'lg:hidden')}>
              {/* <p className="truncate text-[10px] font-medium uppercase tracking-wide text-primary-300">
                Revenue Suite
              </p> */}
            </div>
          </div>
        </Tooltip>

        <GoldRule className="mx-4" color={isBranded ? branding?.accentColor : undefined} />

        {/* Tenant switcher */}
        <div className={cn('px-3 pt-3.5', collapsed && 'lg:px-2')}>
          <Tooltip label={tenant.data?.name ?? 'Workspace'} disabled={!collapsed}>
            <button
              type="button"
              className={cn(
                'flex w-full items-center gap-2 rounded-md bg-white/[0.06] px-2.5 py-2 text-left transition-colors hover:bg-white/[0.09]',
                collapsed && 'lg:justify-center lg:px-0',
              )}
            >
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-brass-500 text-[10px] font-bold text-primary-900">
                {initials(tenant.data?.name)}
              </span>
              <span className={cn('min-w-0 flex-1 leading-tight', collapsed && 'lg:hidden')}>
                <span className="block truncate text-[11.5px] font-semibold text-white">
                  {tenant.data?.name ?? 'Workspace'}
                </span>
                <span className="block truncate text-[10px] capitalize text-primary-300">
                  {tenant.data?.plan ? `${tenant.data.plan} plan` : '—'}
                </span>
              </span>
              <svg className={cn('h-3.5 w-3.5 shrink-0 text-primary-300', collapsed && 'lg:hidden')} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="m8 9 4-4 4 4M8 15l4 4 4-4" />
              </svg>
            </button>
          </Tooltip>
        </div>

        <nav className="scrollbar-dark flex-1 overflow-y-auto px-3 py-4">
          {sections.map((section, i) => (
            <div key={section.title} className={cn('mb-5', collapsed && 'lg:mb-3')}>
              {collapsed && i > 0 ? (
                <div className="mx-2 mb-3 hidden h-px bg-white/10 lg:block" />
              ) : (
                <p className={cn('mb-1.5 px-2.5 text-[10px] font-bold uppercase tracking-wider text-primary-400', collapsed && 'lg:hidden')}>
                  {section.title}
                </p>
              )}
              <ul className="space-y-0.5">
                {section.items.map((item) => (
                  <li key={item.to}>
                    <Tooltip label={item.label} disabled={!collapsed}>
                      <NavLink
                        to={item.to}
                        end={item.to === '/' || item.end}
                        onClick={close}
                        className={({ isActive }) =>
                          cn(
                            'group relative flex items-center gap-2.5 rounded-md py-2 pl-2.5 pr-2 text-[13.5px] transition-colors',
                            collapsed && 'lg:justify-center lg:px-0',
                            isActive
                              ? 'bg-white/[0.09] font-semibold text-white'
                              : 'text-primary-200 hover:bg-white/[0.06] hover:text-white',
                          )
                        }
                      >
                        {({ isActive }) => (
                          <>
                            {/* Brass active-accent bar (#C79A3C, 2.5px) */}
                            <span
                              className={cn(
                                'absolute inset-y-1.5 left-0 w-[2.5px] rounded-[2px] bg-brass-500 transition-opacity',
                                isActive ? 'opacity-100' : 'opacity-0',
                              )}
                            />
                            <span className={cn('shrink-0', isActive && 'text-brass-500')}>
                              {NAV_ICONS[item.icon]}
                            </span>
                            <span className={cn('flex-1 truncate', collapsed && 'lg:hidden')}>{item.label}</span>
                            {item.planned && (
                              <span className={cn('shrink-0 rounded bg-white/10 px-1.5 py-0.5 text-[8.5px] font-bold uppercase tracking-wide text-primary-200', collapsed && 'lg:hidden')}>
                                Soon
                              </span>
                            )}
                          </>
                        )}
                      </NavLink>
                    </Tooltip>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {/* Collapse/expand handle — floats on the rail's edge, desktop only; mobile
            uses the header's drawer toggle instead. */}
        <Tooltip label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
          <button
            type="button"
            onClick={() => dispatch(toggleSidebarCollapsed())}
            className={cn(
              'absolute top-1/2 z-50 hidden h-6 w-6 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-primary-700 text-primary-200 shadow-card transition-[left,transform,background-color,color] duration-200 hover:border-brass-500/50 hover:bg-primary-500 hover:text-white lg:flex',
              collapsed ? 'left-16' : 'left-52',
            )}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <svg
              className={cn('h-3 w-3 shrink-0 transition-transform', collapsed && 'rotate-180')}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m15 6-6 6 6 6" />
            </svg>
          </button>
        </Tooltip>

        {/* Signed-in user */}
        <GoldRule className="mx-4" />
        <div className={cn('flex items-center gap-2.5 p-3', collapsed && 'lg:justify-center lg:px-2')}>
          <Tooltip label={user?.fullName ?? 'Account'} disabled={!collapsed}>
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-700 text-[11px] font-bold text-white ring-1 ring-white/10">
              {initials(user?.fullName)}
            </span>
          </Tooltip>
          <div className={cn('min-w-0 flex-1 leading-tight', collapsed && 'lg:hidden')}>
            <p className="truncate text-[12px] font-semibold text-white">{user?.fullName ?? '—'}</p>
            <p className="truncate text-[10.5px] text-primary-300">{user?.email ?? ''}</p>
          </div>
          <Link
            to="/account/security"
            className={cn('shrink-0 rounded-md p-1.5 text-primary-300 transition-colors hover:bg-white/10 hover:text-white', collapsed && 'lg:hidden')}
            aria-label="Security settings"
            title="Security settings"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
            </svg>
          </Link>
          <button
            type="button"
            onClick={() => void logout()}
            className={cn('shrink-0 rounded-md p-1.5 text-primary-300 transition-colors hover:bg-white/10 hover:text-white', collapsed && 'lg:hidden')}
            aria-label="Sign out"
            title="Sign out"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <path d="m16 17 5-5-5-5M21 12H9" />
            </svg>
          </button>
        </div>
      </aside>
    </>
  );
}

function initials(name?: string | null): string {
  if (!name) return '?';
  return name
    .trim()
    .split(/\s+/)
    .map((w) => w[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

/** "#RRGGBB" -> "r,g,b"; falls back to the default gold on a malformed value. */
function hexToRgb(hex: string): string {
  const m = /^#([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/.exec(hex);
  if (!m) return '199,154,60';
  return [m[1] ?? '00', m[2] ?? '00', m[3] ?? '00'].map((h) => parseInt(h, 16)).join(',');
}

/** The design's gold dashed rule (meridian-rule) — brass dashes, not a grey line. */
function GoldRule({ className, color }: { className?: string; color?: string }) {
  const rgb = color ? hexToRgb(color) : '199,154,60';
  return (
    <div
      className={className}
      style={{
        height: '1px',
        backgroundImage: `repeating-linear-gradient(90deg, rgba(${rgb},0.55) 0 6px, transparent 6px 12px)`,
      }}
    />
  );
}

/** Line icons (16px, stroke-2) for each nav item, matching the shell designs. */
const ic = 'h-4 w-4';
const props = { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };

const NAV_ICONS: Record<NavIcon, ReactNode> = {
  dashboard: (
    <svg className={ic} {...props}><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></svg>
  ),
  ai: (
    <svg className={ic} {...props}><path d="M12 3v3m0 12v3M5.6 5.6l2.1 2.1m8.6 8.6 2.1 2.1M3 12h3m12 0h3M5.6 18.4l2.1-2.1m8.6-8.6 2.1-2.1" /><circle cx="12" cy="12" r="3.2" /></svg>
  ),
  aiAssistant: (
    <svg className={ic} {...props}><path d="M21 12a8 8 0 0 1-8 8H5.5L3 22l1-4.2A8 8 0 1 1 21 12Z" /></svg>
  ),
  projects: (
    <svg className={ic} {...props}><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" /></svg>
  ),
  tasks: (
    <svg className={ic} {...props}><rect x="4" y="4" width="16" height="16" rx="2.5" /><path d="m8.5 12 2.2 2.2 4.5-4.5" /></svg>
  ),
  calendar: (
    <svg className={ic} {...props}><rect x="3" y="4.5" width="18" height="16" rx="2.5" /><path d="M3 9h18M8 3v3m8-3v3" /></svg>
  ),
  crm: (
    <svg className={ic} {...props}><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
  ),
  deals: (
    <svg className={ic} {...props}><path d="M3 3v18h18" /><path d="m7 14 3.5-3.5 3 3L21 7" /></svg>
  ),
  territory: (
    <svg className={ic} {...props}><path d="M12 21s-7-6.1-7-11.5A7 7 0 0 1 19 9.5C19 14.9 12 21 12 21Z" /><circle cx="12" cy="9.5" r="2.5" /></svg>
  ),
  pipeline: (
    <svg className={ic} {...props}><path d="M4 4h16l-5.5 7.5v6.5l-5-2v-4.5Z" /></svg>
  ),
  sequences: (
    <svg className={ic} {...props}><path d="M4 5.5h16a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-11a1 1 0 0 1 1-1Z" /><path d="m3.5 6 8.5 6.5L20.5 6" /></svg>
  ),
  quotations: (
    <svg className={ic} {...props}><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z" /><path d="M14 3v5h5M9 13h6M9 17h6" /></svg>
  ),
  hrms: (
    <svg className={ic} {...props}><path d="M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9.5" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13A4 4 0 0 1 16 11" /></svg>
  ),
  finance: (
    <svg className={ic} {...props}><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M3 11h18M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
  ),
  documents: (
    <svg className={ic} {...props}><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z" /><path d="M14 3v5h5" /></svg>
  ),
  assets: (
    <svg className={ic} {...props}><path d="M20 17.5A2.5 2.5 0 0 0 17.5 15H8a3 3 0 1 1 .5-5.96A4.5 4.5 0 1 1 17 10.5a3.5 3.5 0 0 1 3 3.5Z" /></svg>
  ),
  inventory: (
    <svg className={ic} {...props}><path d="M21 8 12 3 3 8l9 5 9-5Z" /><path d="M3 8v8l9 5 9-5V8" /><path d="M12 13v8" /></svg>
  ),
  procurement: (
    <svg className={ic} {...props}><path d="M6 6h15l-1.5 9h-12z" /><path d="M6 6 5 3H2" /><circle cx="9.5" cy="20" r="1.5" /><circle cx="17.5" cy="20" r="1.5" /></svg>
  ),
  generalLedger: (
    <svg className={ic} {...props}><path d="M4 4h13a3 3 0 0 1 3 3v13H7a3 3 0 0 1-3-3z" /><path d="M4 17a3 3 0 0 1 3-3h13" /><path d="M9 8h7M9 11h7" /></svg>
  ),
  reports: (
    <svg className={ic} {...props}><path d="M3 3v18h18" /><rect x="7" y="12" width="3" height="6" /><rect x="12" y="8" width="3" height="10" /><rect x="17" y="5" width="3" height="13" /></svg>
  ),
  workflow: (
    <svg className={ic} {...props}><circle cx="6" cy="6" r="2.5" /><circle cx="18" cy="18" r="2.5" /><path d="M8.5 6H15a3 3 0 0 1 3 3v6" /></svg>
  ),
  settings: (
    <svg className={ic} {...props}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-2.82 1.17V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 7.5 19.4l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 5 12.6H4.91a2 2 0 1 1 0-4H5a1.65 1.65 0 0 0 1.51-1.09" /></svg>
  ),
  roles: (
    <svg className={ic} {...props}><path d="M12 2 4 5v6c0 5 3.4 8.7 8 11 4.6-2.3 8-6 8-11V5Z" /><path d="m9 12 2 2 4-4" /></svg>
  ),
  users: (
    <svg className={ic} {...props}><circle cx="9" cy="8" r="3.2" /><path d="M2.8 20c0-3.5 2.8-6.2 6.2-6.2s6.2 2.7 6.2 6.2" /><path d="M16 5.5a3.2 3.2 0 0 1 0 6.2" /><path d="M18.5 14.3c2.2.6 3.7 2.6 3.7 5.7" /></svg>
  ),
  billing: (
    <svg className={ic} {...props}><rect x="2" y="5" width="20" height="14" rx="2.5" /><path d="M2 10h20" /></svg>
  ),
  portal: (
    <svg className={ic} {...props}><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18" /><circle cx="8" cy="14" r="1.5" /><path d="M12 14h6" /></svg>
  ),
  audit: (
    <svg className={ic} {...props}><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z" /><path d="M14 3v5h5M9 13h6M9 17h4" /></svg>
  ),
  agency: (
    <svg className={ic} {...props}><path d="M3 11v3a1 1 0 0 0 1 1h2l4 4V6L6 10H4a1 1 0 0 0-1 1Z" /><path d="M15 8a4 4 0 0 1 0 8M18.5 5a8 8 0 0 1 0 14" /></svg>
  ),
  inbox: (
    <svg className={ic} {...props}><rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="m3.5 6 8.5 6.5L20.5 6" /></svg>
  ),
  whatsapp: (
    <svg className={ic} {...props}><path d="M12 3a9 9 0 0 0-7.79 13.5L3 21l4.6-1.2A9 9 0 1 0 12 3Z" /><path d="M9 10c0 2.8 2.2 5 5 5" /></svg>
  ),
  marketplace: (
    <svg className={ic} {...props}><path d="M4 8h16l-1.5 9a2 2 0 0 1-2 1.7H7.5a2 2 0 0 1-2-1.7Z" /><path d="M8 8V6a4 4 0 0 1 8 0v2" /></svg>
  ),
  holidays: (
    <svg className={ic} {...props}><rect x="3" y="4.5" width="18" height="16" rx="2.5" /><path d="M3 9h18M8 3v3m8-3v3" /><path d="m8.5 15 2.2 2.2 4.5-4.5" /></svg>
  ),
  attendance: (
    <svg className={ic} {...props}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.5 2" /></svg>
  ),
  leave: (
    <svg className={ic} {...props}><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z" /><path d="M14 3v5h5" /><path d="m9.5 15 2 2 3-3" /></svg>
  ),
  remoteWork: (
    <svg className={ic} {...props}><rect x="2.5" y="5.5" width="19" height="12" rx="2" /><path d="M8 21h8M12 17.5V21" /></svg>
  ),
  payslips: (
    <svg className={ic} {...props}><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z" /><path d="M14 3v5h5M9 13h6M9 17h3" /></svg>
  ),
};
